/**
 * ON-02a — how a KYC document reads on screen. Pure, so it can be tested without rendering.
 *
 * Three small translations from what the API stores to what a person reads, kept out of the
 * component for the usual reason: the format label is a mapping from a MIME type that the
 * component would otherwise do inline, and an inline mapping with a fallback is where "unknown"
 * quietly becomes the common case.
 */

/**
 * The format, in the word the client used — "PDF", "JPG", "DOCX".
 *
 * Keyed on what the SERVER detected from the bytes, so the label describes the file and not its
 * name. A type that is not in this map cannot currently be stored (`sniffDocumentBytes` refuses
 * it), so reaching the fallback means the API has learned a format this screen has not: it shows
 * the type itself rather than "Unknown", because the honest answer to "what is this file" is the
 * type, and a row that says Unknown invites somebody to delete a perfectly good document.
 */
const FORMATS: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
};

export function documentFormat(contentType: string): string {
  return FORMATS[contentType] ?? contentType;
}

/**
 * The size, in the unit a person would say.
 *
 * KB below a megabyte and MB above it, one decimal place. Not `toLocaleString` on a byte count:
 * "1,482,391 bytes" is a number nobody compares against an 8 MB limit at a glance.
 */
export function documentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)} MB`;
}

/**
 * Who filed it, as one of two label keys.
 *
 * `uploaded_by` holds the literal `'APPLICANT'` or an internal user's uuid, so anything that is
 * not the literal is somebody here. Collapsed to two cases on purpose rather than resolving the
 * uuid to a name: the question this column answers is *which kind of evidence is this*, and the
 * audit log already names the person. One extra lookup per row to say "K. Mercer" would also put
 * a staff name on a screen whose other columns are all about the applicant.
 */
export function uploadedByWords(
  uploadedBy: string,
  /** CR-57 — `ACCOUNT` means the retailer filed it themselves after signing in. Absent on A-02's list. */
  source?: 'APPLICATION' | 'ACCOUNT',
): 'APPLICANT' | 'RETAILER' | 'MIRAKI' {
  // Checked FIRST, because a retailer-filed document's `uploaded_by` is a user id — which the rule
  // below would read as "somebody here" and credit to Miraki.
  if (source === 'ACCOUNT') return 'RETAILER';
  return uploadedBy === 'APPLICANT' ? 'APPLICANT' : 'MIRAKI';
}

/**
 * CR-57 P3 / D105 — what A-04 says about where the account's KYC stands. Admin-facing, so plain
 * English rather than catalogue keys.
 *
 * PENDING means two things — nothing has been sent, or something has and nobody has decided — and
 * the reviewer needs to know which: one is waiting for the retailer, the other is waiting for them.
 */
export type KycWords = 'Approved' | 'Rejected' | 'Awaiting review' | 'Nothing submitted';
export function kycWords(status: 'PENDING' | 'APPROVED' | 'REJECTED', documents: number): KycWords {
  if (status === 'APPROVED') return 'Approved';
  if (status === 'REJECTED') return 'Rejected';
  return documents === 0 ? 'Nothing submitted' : 'Awaiting review';
}

/**
 * The line that answers "who verified this business, and when" — the question that gets asked a
 * year later. Empty until a decision exists. A PENDING account that carries an old reason was
 * rejected and has since filed again, and the line says so rather than showing it as undecided.
 */
export function reviewLine(
  status: 'PENDING' | 'APPROVED' | 'REJECTED',
  reviewedAt: string | null,
  reviewer: string | null,
  rejectionReason: string | null,
): string | null {
  if (!reviewedAt) return null;
  const on = reviewedAt.slice(0, 10);
  const by = reviewer ?? 'a Miraki user';
  if (status === 'APPROVED') return `Approved by ${by} on ${on}.`;
  if (status === 'REJECTED') return `Rejected by ${by} on ${on}.`;
  return rejectionReason ? `Rejected by ${by} on ${on}; the retailer has filed again since.` : null;
}

/**
 * Approving with nothing on file is ALLOWED (D105 Q1 — the old refusal became a warning), so the
 * screen's job is to make sure the person pressing the button knows that is what they are doing.
 */
export function approvalWarning(documents: number): string | null {
  return documents === 0
    ? 'There are no documents on file. Approving verifies this business without any evidence, and the decision is recorded against your name.'
    : null;
}

/**
 * A-02 — shown BEFORE Admin approves an application that has no KYC documents attached.
 *
 * 🔴 This used to be a refusal (D94: "no approval without KYC evidence"). **D105 (03-Nov-2026)
 * superseded D94: KYC now gates the ORDER, not the account**, so approving with nothing on file is
 * allowed and this is a warning, not a block. Do not turn it back into a disabled button: it will
 * look like a missing validation and it is not — it would stop Admin approving the accounts D105 Q1
 * says they may. The API's `approvalDocumentWarning()` says the same thing after the fact; this is the
 * before-the-click half, and the two are kept word for word.
 *
 * It says what happens next — that is the part an approver needs: the retailer files their own pack
 * after signing in and cannot order until it is approved on their account (A-04).
 */
export function applicationApprovalWarning(documents: number): string | null {
  return documents === 0
    ? 'No KYC documents are attached to this application. You can still approve it (D105). The retailer '
      + 'will file their own documents after signing in, and cannot submit an order until you have '
      + 'approved them on their account.'
    : null;
}

